import {
  EditableCancelTrigger,
  EditableControl,
  EditableEditTrigger,
  EditableInput,
  EditablePreview,
  EditableRoot,
  EditableRootProps,
  EditableSubmitTrigger,
  EditableTextarea,
  EditableValueChangeDetails,
  IconButton,
  IconButtonProps,
  Spinner,
} from "@chakra-ui/react";
import {CheckIcon, PencilSquareIcon, XMarkIcon} from "@heroicons/react/16/solid";
import {memo, useCallback} from "react";
import {AutoResizingTextarea} from "./AutoResizingTextarea";

export type EditableProps = EditableRootProps & {
  /** Applies to all control buttons. */
  controlButtonProps?: IconButtonProps;

  editButtonProps?: IconButtonProps;
  cancelButtonProps?: IconButtonProps;
  submitButtonProps?: IconButtonProps;

  loading?: boolean;

  withControls?: boolean;

  /**
   * Break a value that carries no break opportunity, so it wraps to its
   * container instead of setting the container's minimum width. The preview
   * recipe already asks for `break-word`, which breaks the painted text but
   * leaves min-content at the whole string — the width a flex item's
   * `min-width: auto` resolves to, and therefore overflows with.
   */
  wrapAnywhere?: boolean;

  /**
   * Edit in an auto-growing textarea rather than a single-line input, with the
   * preview rendering the value's line breaks. Enter inserts a newline, so the
   * edit commits on blur or the ✓ control instead (overridable via `submitMode`).
   */
  multiline?: boolean;
};

export const Editable = memo(
  ({
    controlButtonProps,
    editButtonProps,
    cancelButtonProps,
    submitButtonProps,
    loading,
    disabled,
    onValueCommit,
    value,
    withControls,
    wrapAnywhere,
    multiline,
    submitMode,
    ...props
  }: EditableProps) => {
    const disabledInner = loading || disabled;

    // With no activation, the preview is plain text: no hover tint, no text
    // cursor, since neither leads anywhere.
    const inert = props.activationMode === "none";

    const onValueCommitInner = useCallback(
      (d: EditableValueChangeDetails) => {
        onValueCommit?.(d);
      },
      [onValueCommit],
    );

    return (
      <EditableRoot
        {...props}
        submitMode={submitMode ?? (multiline ? "blur" : undefined)}
        disabled={disabledInner}
        defaultValue={value}
        onValueCommit={onValueCommitInner}
      >
        <EditablePreview
          overflowWrap={wrapAnywhere ? "anywhere" : undefined}
          whiteSpace={multiline ? "pre-wrap" : undefined}
          cursor={inert ? "inherit" : undefined}
          _hover={inert ? {bg: "transparent"} : undefined}
        />
        {multiline ? (
          <EditableTextarea asChild>
            <AutoResizingTextarea bg="bg" minRows={1} maxRows={12} />
          </EditableTextarea>
        ) : (
          <EditableInput bg="bg" />
        )}
        {loading ? <Spinner ml={2} size="xs" color="fg.muted" /> : <></>}
        {withControls && (
          <EditableControl>
            <EditableEditTrigger asChild>
              <IconButton variant="ghost" size="xs" {...controlButtonProps} {...editButtonProps}>
                <PencilSquareIcon />
              </IconButton>
            </EditableEditTrigger>
            <EditableCancelTrigger asChild>
              <IconButton variant="ghost" size="xs" {...controlButtonProps} {...cancelButtonProps}>
                <XMarkIcon />
              </IconButton>
            </EditableCancelTrigger>
            <EditableSubmitTrigger asChild>
              <IconButton variant="ghost" size="xs" {...controlButtonProps} {...submitButtonProps}>
                <CheckIcon />
              </IconButton>
            </EditableSubmitTrigger>
          </EditableControl>
        )}
      </EditableRoot>
    );
  },
);
